import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useApiClient } from '../react/api-context.tsx';
import { invalidationsFor } from './event-keys.ts';
import { LiveConnection, type LiveChange, type LiveConnectionOptions, type LiveStatus } from './live-connection.ts';

export type LiveHandlers = {
  /** Every `change`, after its queries were invalidated. */
  onChange?: (event: LiveChange) => void;
  /** By event type, e.g. `{ 'store_order.created': ring }`. */
  on?: Record<string, (event: LiveChange) => void>;
  onResync?: () => void;
  onReady?: (info: { reconnected: boolean }) => void;
  onStatus?: (status: LiveStatus) => void;
};

export type LiveState = { status: LiveStatus; since: number };

type Tuning = Pick<LiveConnectionOptions, 'initialDelayMs' | 'maxDelayMs' | 'idleTimeoutMs'>;

/**
 * Keeps the surface's live stream open while mounted (`path` relative to `/v1`, e.g.
 * `/admin/events`; null pauses it). Each `change` invalidates its queries; `resync` and every
 * reconnection invalidate them all, because events may have been missed meanwhile.
 */
export function useLiveEvents(path: string | null, handlers: LiveHandlers = {}, tuning: Tuning = {}): LiveState {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const [state, setState] = useState<LiveState>({ status: path ? 'connecting' : 'stopped', since: Date.now() });
  const { initialDelayMs, maxDelayMs, idleTimeoutMs } = tuning;

  useEffect(() => {
    if (!path) {
      setState({ status: 'stopped', since: Date.now() });
      return;
    }
    const connection = new LiveConnection({
      client,
      path,
      initialDelayMs,
      maxDelayMs,
      idleTimeoutMs,
      onChange: event => {
        for (const queryKey of invalidationsFor(event.type)) void queryClient.invalidateQueries({ queryKey });
        handlersRef.current.onChange?.(event);
        handlersRef.current.on?.[event.type]?.(event);
      },
      onResync: () => {
        void queryClient.invalidateQueries();
        handlersRef.current.onResync?.();
      },
      onReady: info => {
        if (info.reconnected) void queryClient.invalidateQueries();
        handlersRef.current.onReady?.(info);
      },
      onStatus: status => {
        setState({ status, since: Date.now() });
        handlersRef.current.onStatus?.(status);
      },
    });
    const unsubscribe = client.subscribeSession(() => connection.stop());
    connection.start();
    return () => { unsubscribe(); connection.stop(); };
  }, [path, client, queryClient, initialDelayMs, maxDelayMs, idleTimeoutMs]);

  return state;
}

/**
 * True when the stream has been down for longer than `graceMs` (a short blip is not worth a big
 * notice). The business page shows «Sin conexión» with it.
 */
export function useLiveLost(state: LiveState, graceMs = 5000): boolean {
  const down = state.status === 'offline' || state.status === 'reconnecting';
  const [lost, setLost] = useState(false);
  useEffect(() => {
    if (!down) {
      setLost(false);
      return;
    }
    const left = graceMs - (Date.now() - state.since);
    if (left <= 0) {
      setLost(true);
      return;
    }
    const timer = setTimeout(() => setLost(true), left);
    return () => clearTimeout(timer);
  }, [down, state.since, graceMs]);
  return lost;
}
